import React, {useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import Chips, {ChipsTypes} from "../../../../../../../../components/base/chips/chips";
import {OutlinedInput} from "../../../../../../../../components/base/input/outlined_input";
import {dataGridCellTextProps} from "../../../../../../../../../packages/data-grid";
import {ReactComponent as EnterIcon} from "../../../../../../../../../assets/images/products/enter.svg";

/**
 * Purchase-order serial chips: each add / remove delegates to an API callback.
 * Capped by maxCount (order qty).
 */
const PurchaseOrderSerialNumberEntry = ({
                                            serials = [],
                                            onAdd,
                                            onRemove,
                                            maxCount = 0,
                                            disabled = false,
                                        }) => {
    const {t} = useTranslation('views', {
        keyPrefix: 'app-specific.purchases.orderSingle.products.serials',
    });
    const [draft, setDraft] = useState('');
    const [committing, setCommitting] = useState(false);

    const canAdd = !disabled && !committing && serials.length < maxCount;

    /**
     * Normalizes serial entries to strings for display / equality checks.
     */
    const normalizedSerials = useMemo(() => (
        (Array.isArray(serials) ? serials : []).map((entry) => (
            typeof entry === 'string' ? entry : (entry?.serialNumber ?? '')
        )).filter(Boolean)
    ), [serials]);

    /**
     * Commits draft via add API when under cap and unique.
     */
    const commitDraft = useCallback(async () => {
        const next = (draft ?? '').toString().trim();
        if (!next || !canAdd) {
            return;
        }
        if (normalizedSerials.includes(next)) {
            setDraft('');
            return;
        }
        setCommitting(true);
        try {
            await onAdd?.(next);
            setDraft('');
        } finally {
            setCommitting(false);
        }
    }, [canAdd, draft, normalizedSerials, onAdd]);

    /**
     * Removes one serial via remove API.
     */
    const removeSerial = useCallback(async (serial) => {
        if (disabled || committing) {
            return;
        }
        setCommitting(true);
        try {
            await onRemove?.(serial);
        } finally {
            setCommitting(false);
        }
    }, [committing, disabled, onRemove]);

    const chipsType = disabled
        ? ChipsTypes.none
        : committing
            ? ChipsTypes.loading
            : ChipsTypes.remove;

    const serialChips = useMemo(() => (
        normalizedSerials.map((serial) => (
            <span key={serial} {...dataGridCellTextProps}>
                <Chips
                    type={chipsType}
                    content={serial}
                    onIconClick={() => void removeSerial(serial)}
                />
            </span>
        ))
    ), [chipsType, normalizedSerials, removeSerial]);

    return (
        <div
            className={'d-flex flex-column w-100'}
            style={{gap: 8, minWidth: 180, padding: '4px 0'}}
            onClick={(e) => e.stopPropagation()}
        >
            <div className={'d-flex w-100 flex-wrap align-items-center'} style={{gap: 8}}>
                {serialChips}
            </div>
            {
                canAdd &&
                <OutlinedInput
                    size={'small'}
                    value={draft}
                    onChange={setDraft}
                    disabled={committing}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            void commitDraft();
                        }
                    }}
                    placeholder={t('placeholder', {max: maxCount})}
                    icon={<EnterIcon/>}
                    onIconClick={() => void commitDraft()}
                    iconButtonProps={{
                        'aria-label': t('addAriaLabel'),
                        disabled: committing || !(draft ?? '').toString().trim(),
                    }}
                    fullWidth
                />
            }
        </div>
    );
};

export default PurchaseOrderSerialNumberEntry;
